// PaymentMethod.js
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Box, Typography, Button, CircularProgress, Alert, List, ListItem, ListItemText, ListItemSecondaryAction, IconButton, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Card, CardContent, Divider , Chip } from '@mui/material';
import { AddCircleOutline as AddIcon, Delete as DeleteIcon, CheckCircle as DefaultIcon, RadioButtonUnchecked as NotDefaultIcon } from '@mui/icons-material';

// Stripe Imports
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import backendURL, { stripe_publishable_key } from "../../config"; // Assuming config exports these

// --- Configuration ---
const API_BASE_URL = backendURL;
const STRIPE_PKEY = stripe_publishable_key;

if (!STRIPE_PKEY || STRIPE_PKEY === 'pk_test_YOUR_PUBLISHABLE_KEY') {
    console.warn("Stripe Publishable Key is not set or is using the default placeholder in PaymentMethod.js.");
}
const stripePromise = STRIPE_PKEY ? loadStripe(STRIPE_PKEY) : Promise.resolve(null);

// --- Helper Components ---
const LoadingSpinner = ({ size = 24 }) => ( // MUI uses size prop directly
    <CircularProgress size={size} />
);

// --- Internal Stripe Form for Adding Card ---
const InternalAddCardForm = ({ clientSecret, onSuccess, onError, onCancel }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [message, setMessage] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);

    const callAttachPaymentMethodAPI = async (paymentMethodId) => {
        const token = localStorage.getItem('access_token');
        const headers = { 'Content-Type': 'application/json', ...(token && { 'Authorization': `Bearer ${token}` }) };
        // Backend endpoint to associate the Stripe PM ID with the user
        return axios.post(`${API_BASE_URL}/payment-methods`, { gateway_payment_method_id: paymentMethodId }, { headers });
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!stripe || !elements) {
            const errMsg = "Stripe has not loaded yet.";
            setMessage(errMsg);
            if (onError) onError(errMsg);
            return;
        }
        setIsProcessing(true);
        setMessage(null);

        const { error: setupError, setupIntent } = await stripe.confirmSetup({
            elements,
            confirmParams: {}, // No specific confirm params needed for setup usually
            redirect: 'if_required', // Don't redirect away from the page
        });

        if (setupError) {
            const errMsg = `Payment setup failed: ${setupError.message}`;
            setMessage(errMsg);
            setIsProcessing(false);
            if (onError) onError(errMsg);
            return;
        }

        if (setupIntent?.status === 'succeeded') {
            const paymentMethodId = setupIntent.payment_method;
            if (!paymentMethodId) {
                const errMsg = "Payment method details missing after setup.";
                setMessage(errMsg);
                setIsProcessing(false);
                if (onError) onError(errMsg);
                return;
            }
            try {
                setMessage("Card details secured. Saving to your account...");
                // Call backend to officially attach this PM to the user
                await callAttachPaymentMethodAPI(paymentMethodId);
                setMessage("Payment method added successfully!");
                // Short delay before calling onSuccess to let user read message
                setTimeout(() => {
                    if (onSuccess) onSuccess(); // Signal success to parent
                }, 1500);
            } catch (apiError) {
                console.error("Backend attach PM call failed:", apiError);
                const errMsg = apiError.response?.data?.message || apiError.message || "Failed to save the new payment method to your account.";
                setMessage(errMsg);
                setIsProcessing(false);
                if (onError) onError(errMsg);
            }
        } else {
            const errMsg = `Card setup status: ${setupIntent?.status}. Please try again or use a different card.`;
            setMessage(errMsg);
            setIsProcessing(false);
            if (onError) onError(errMsg);
        }
    };

    const paymentElementOptions = { layout: "tabs" };

    return (
        <form onSubmit={handleSubmit}>
            <Typography variant="h6" gutterBottom>Add New Payment Card</Typography>
            <PaymentElement id="payment-element" options={paymentElementOptions} />
            {message && (
                <Alert severity={message.includes('failed') || message.includes('missing') || message.includes('try again') ? "error" : "info"} sx={{ mt: 2 }}>
                    {message}
                </Alert>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
                <Button onClick={onCancel} disabled={isProcessing} color="secondary">
                    Cancel
                </Button>
                <Button
                    type="submit"
                    disabled={isProcessing || !stripe || !elements}
                    variant="contained"
                    startIcon={isProcessing ? <LoadingSpinner size={20} /> : null}
                >
                    {isProcessing ? 'Processing...' : 'Add Card'}
                </Button>
            </Box>
        </form>
    );
};


// --- Main Payment Method Component ---
const PaymentMethod = ({ onActionComplete, onActionError }) => {
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showAddCardForm, setShowAddCardForm] = useState(false);
    const [clientSecret, setClientSecret] = useState(null);
    const [isAddingCardLoading, setIsAddingCardLoading] = useState(false); // For setup intent fetch
    const [actionLoading, setActionLoading] = useState({ type: null, id: null }); // { type: 'delete'/'setDefault', id: 'pm_...' }

    // State for delete confirmation
    const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState(false);
    const [cardToDelete, setCardToDelete] = useState(null); // Store the { id, brand, last4 } object

    const getAuthHeaders = useCallback(() => {
        const token = localStorage.getItem('access_token');
        if (!token) {
            console.error("Authentication token not found.");
            // Handle missing token - maybe call onActionError or redirect
            if (onActionError) onActionError("Authentication token not found. Please log in again.");
            return null;
        }
        return { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` };
    }, [onActionError]);

    const fetchPaymentMethods = useCallback(async () => {
        const headers = getAuthHeaders();
        if (!headers) return; // Stop if no token

        setLoading(true);
        setError(null);
        try {
            const response = await axios.get(`${API_BASE_URL}/payment-methods`, { headers });
            // Sort methods so the default one is first, then by brand/last4
            const sortedMethods = (response.data || []).sort((a, b) => {
                if (a.is_default && !b.is_default) return -1;
                if (!a.is_default && b.is_default) return 1;
                if (a.card_brand < b.card_brand) return -1;
                if (a.card_brand > b.card_brand) return 1;
                if (a.last4 < b.last4) return -1;
                if (a.last4 > b.last4) return 1;
                return 0;
            });
            setPaymentMethods(sortedMethods);
        } catch (err) {
            console.error("Error fetching payment methods:", err);
            const errMsg = err.response?.data?.message || err.message || "Failed to load payment methods.";
            setError(errMsg);
            if (onActionError) onActionError(errMsg); // Notify parent of fetch failure
        } finally {
            setLoading(false);
        }
    }, [getAuthHeaders, onActionError]); // Added onActionError dependency

    useEffect(() => {
        fetchPaymentMethods();
    }, [fetchPaymentMethods]); // Run fetch on mount and when fetch function identity changes (due to token potentially)

    // --- Handlers ---

    const handleAddCardClick = async () => {
        const headers = getAuthHeaders();
        if (!headers || !stripePromise) return;

        setIsAddingCardLoading(true);
        setError(null); // Clear previous errors
        setShowAddCardForm(false); // Hide form initially
        setClientSecret(null);

        try {
            const response = await axios.post(`${API_BASE_URL}/payment-methods/setup-intent`, {}, { headers });
            if (response.data?.client_secret) {
                setClientSecret(response.data.client_secret);
                setShowAddCardForm(true); // Show form only after getting secret
            } else {
                throw new Error("Client secret not received from setup intent.");
            }
        } catch (err) {
            console.error("Setup Intent fetch failed:", err);
            const errMsg = err.response?.data?.message || "Could not initialize payment method setup.";
            setError(errMsg); // Show error locally
            if (onActionError) onActionError(errMsg); // Notify parent
        } finally {
            setIsAddingCardLoading(false);
        }
    };

    const handleCancelAddCard = () => {
        setShowAddCardForm(false);
        setClientSecret(null);
        setError(null); // Clear any setup-intent fetch errors
    };

    const handleCardSetupSuccess = () => {
        setShowAddCardForm(false);
        setClientSecret(null);
        if (onActionComplete) onActionComplete("Payment method added successfully!"); // Notify parent
        fetchPaymentMethods(); // Refresh the list
    };

    const handleCardSetupError = (errorMessage) => {
        // Error message is already displayed within InternalAddCardForm
        // We might want to notify the parent as well, or just let the local form handle it.
        // setError(errorMessage); // Optionally set local error too, though form shows it
        if (onActionError) onActionError(errorMessage); // Notify parent
        // Keep the form open for the user to see the error / retry
    };

    const handleSetDefault = async (methodId) => {
        const headers = getAuthHeaders();
        if (!headers) return;

        setActionLoading({ type: 'setDefault', id: methodId });
        setError(null);
        try {
            await axios.post(`${API_BASE_URL}/payment-methods/${methodId}/default`, {}, { headers });
            if (onActionComplete) onActionComplete("Default payment method updated.");
            fetchPaymentMethods(); // Refresh the list to show the change
        } catch (err) {
            console.error("Error setting default payment method:", err);
            const errMsg = err.response?.data?.message || "Failed to update default payment method.";
            setError(errMsg);
            if (onActionError) onActionError(errMsg);
        } finally {
            setActionLoading({ type: null, id: null });
        }
    };

    const handleDeleteClick = (method) => {
        setCardToDelete(method); // Store the full method object { id, brand, last4 }
        setConfirmDeleteDialogOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!cardToDelete) return;
        const headers = getAuthHeaders();
        if (!headers) return;

        const methodId = cardToDelete.id;
        setConfirmDeleteDialogOpen(false); // Close dialog immediately
        setActionLoading({ type: 'delete', id: methodId });
        setError(null);

        try {
            await axios.delete(`${API_BASE_URL}/payment-methods/${methodId}`, { headers });
            setCardToDelete(null);
            if (onActionComplete) onActionComplete(`Card ending in ${cardToDelete.last4} removed.`);
            fetchPaymentMethods(); // Refresh list
        } catch (err) {
            console.error("Error deleting payment method:", err);
            const errMsg = err.response?.data?.message || "Failed to remove payment method.";
            // Check if the error is because it's the default for an active sub
             if (err.response?.data?.error_code === 'DELETE_DEFAULT_PM_WITH_ACTIVE_SUB') {
                 setError(`Cannot delete the default payment method while you have an active subscription. Please set another card as default first.`);
             } else {
                setError(errMsg);
             }
            if (onActionError) onActionError(errMsg);
            setCardToDelete(null); // Clear even on error
        } finally {
            setActionLoading({ type: null, id: null });
        }
    };

    const handleCancelDelete = () => {
        setConfirmDeleteDialogOpen(false);
        setCardToDelete(null);
    };

    // --- Render Logic ---
    const stripeOptions = clientSecret ? { clientSecret: clientSecret, appearance: { theme: 'stripe' } } : null;

    return (
        <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}> {/* Add padding */}
            <Typography variant="h5" gutterBottom>
                Payment Methods
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {/* --- Add Card Section --- */}
            {!showAddCardForm && (
                <Button
                    variant="contained"
                    startIcon={isAddingCardLoading ? <LoadingSpinner size={20} /> : <AddIcon />}
                    onClick={handleAddCardClick}
                    disabled={loading || isAddingCardLoading || actionLoading.type !== null}
                    sx={{ mb: 3 }}
                >
                    {isAddingCardLoading ? 'Initializing...' : 'Add New Card'}
                </Button>
            )}

            {showAddCardForm && clientSecret && stripePromise && stripeOptions && (
                <Card variant="outlined" sx={{ mb: 3 }}>
                    <CardContent>
                         <Elements stripe={stripePromise} options={stripeOptions}>
                             <InternalAddCardForm
                                clientSecret={clientSecret}
                                onSuccess={handleCardSetupSuccess}
                                onError={handleCardSetupError} // Let form display specific stripe errors
                                onCancel={handleCancelAddCard}
                             />
                         </Elements>
                    </CardContent>
                </Card>
            )}

            {/* --- Existing Cards List --- */}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>
            ) : paymentMethods.length === 0 && !showAddCardForm ? (
                <Typography sx={{ mt: 2 }}>No payment methods saved.</Typography>
            ) : !showAddCardForm && ( // Don't show list while adding card form is open for clarity
                <Card variant="outlined">
                    <List disablePadding>
                        {paymentMethods.map((method, index) => (
                            <React.Fragment key={method.id}>
                                <ListItem >
                                    <ListItemText
                                        primary={`${method.card_brand?.toUpperCase()} ending in ${method.last4}`}
                                        secondary={`Expires ${String(method.exp_month).padStart(2, '0')}/${method.exp_year}${method.is_default ? ' (Default)' : ''}`}
                                    />
                                    <ListItemSecondaryAction>
                                        {actionLoading.type === 'setDefault' && actionLoading.id === method.id ? (
                                            <LoadingSpinner size={24}/>
                                        ) : !method.is_default ? (
                                            <Button
                                                size="small"
                                                onClick={() => handleSetDefault(method.id)}
                                                disabled={loading || isAddingCardLoading || actionLoading.type !== null}
                                                startIcon={<NotDefaultIcon />}
                                                sx={{ mr: 1 }}
                                            >
                                                Set Default
                                            </Button>
                                        ) : (
                                            <Chip label="Default" icon={<DefaultIcon />} color="success" size="small" sx={{ mr: 1 }}/> // Use Chip for visual default
                                        )}

                                        {actionLoading.type === 'delete' && actionLoading.id === method.id ? (
                                            <LoadingSpinner size={24} />
                                        ) : (
                                            <IconButton
                                                edge="end"
                                                aria-label="delete"
                                                onClick={() => handleDeleteClick(method)}
                                                disabled={loading || isAddingCardLoading || actionLoading.type !== null || (paymentMethods.length === 1 && method.is_default)} // Disable delete if it's the only card AND default
                                                color="error"
                                                title={ (paymentMethods.length === 1 && method.is_default) ? "Cannot delete the only default card" : "Delete Card"}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        )}
                                    </ListItemSecondaryAction>
                                </ListItem>
                                {index < paymentMethods.length - 1 && <Divider component="li" />}
                            </React.Fragment>
                        ))}
                    </List>
                </Card>
            )}

            {/* --- Delete Confirmation Dialog --- */}
            <Dialog
                open={confirmDeleteDialogOpen}
                onClose={handleCancelDelete}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">Confirm Delete</DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        Are you sure you want to remove the card
                        {cardToDelete ? ` ${cardToDelete.card_brand?.toUpperCase()} ending in ${cardToDelete.last4}` : ''}?
                        This action cannot be undone.
                        {cardToDelete?.is_default && ' If this is your default card for an active subscription, the subscription may fail unless another card is set as default.'}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelDelete} color="secondary">
                        Cancel
                    </Button>
                    <Button onClick={handleConfirmDelete} color="error" autoFocus>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default PaymentMethod;
